import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Circle, ArrowRight } from 'lucide-react';
import { Button } from './BrandingSystem';
import { Option } from './QuizQuestion';
interface QuizResultsProps {
  question: string;
  options: Option[];
  selectedOptionId: string;
  correctAnswerId: string;
  percentages: Record<string, number>;
  feedbackTitle: string;
  feedbackBody: string;
  feedbackReferences?: string;
  onNext: () => void;
  isLastQuestion?: boolean;
  compact?: boolean;
}
export function QuizResults({
  question,
  options,
  selectedOptionId,
  correctAnswerId,
  percentages,
  feedbackTitle,
  feedbackBody,
  onNext,
  isLastQuestion,
  compact = false
}: QuizResultsProps) {
  const isCorrect = selectedOptionId === correctAnswerId;
  return <div className="w-full max-w-3xl mx-auto" data-id="element-85">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} className={`bg-gray-50 rounded-xl ${compact ? 'p-3' : 'p-4'} border border-gray-100 shadow-sm`} data-id="element-86">
        <h2 className={`${compact ? 'text-xs md:text-sm' : 'text-sm md:text-base'} font-bold text-brand-primary ${compact ? 'mb-2' : 'mb-3'} leading-tight`} data-id="element-87">
          {question}
        </h2>

        <div className={`grid grid-cols-1 md:grid-cols-2 ${compact ? 'gap-2' : 'gap-3'}`} data-id="element-88">
          {/* Left: Peer results */}
          <div data-id="element-89">
            <p className={`${compact ? 'text-[8px]' : 'text-[9px]'} font-semibold text-text-subtle uppercase tracking-wider ${compact ? 'mb-1' : 'mb-1.5'}`} data-id="element-90">
              What your peers selected:
            </p>
            <div className={compact ? 'space-y-0.5' : 'space-y-1'} data-id="element-91">
              {options.map(option => {
              const isSelected = option.id === selectedOptionId;
              const isCorrectOption = option.id === correctAnswerId;
              const percentage = percentages[option.id] || 0;
              let borderColor = 'border-[#D64045]/50';
              let bgColor = 'bg-[#D64045]/5';
              let textColor = 'text-text';
              let barColor = 'bg-[#D64045]';
              let icon = <Circle className="w-3 h-3 text-[#D64045]/40" data-id="element-92" />;
              if (isCorrectOption && isSelected) {
                borderColor = 'border-[#00857C]';
                bgColor = 'bg-[#00857C]/10';
                barColor = 'bg-[#00857C]';
                icon = <CheckCircle className="w-3 h-3 text-[#00857C] fill-current" data-id="element-93" />;
              } else if (isCorrectOption) {
                borderColor = 'border-[#00857C]';
                bgColor = 'bg-[#00857C]/5';
                barColor = 'bg-[#00857C]';
                icon = <CheckCircle className="w-3 h-3 text-[#00857C]" data-id="element-94" />;
              } else if (isSelected) {
                borderColor = 'border-[#D64045]';
                bgColor = 'bg-[#D64045]/10';
                barColor = 'bg-[#D64045]';
                icon = <XCircle className="w-3 h-3 text-[#D64045] fill-current" data-id="element-95" />;
              }
              return <div key={option.id} className={`relative w-full rounded border ${borderColor} ${bgColor} overflow-hidden ${isSelected ? 'ring-1 ring-offset-1 ' + (isCorrectOption ? 'ring-[#00857C]/40' : 'ring-[#D64045]/40') : ''}`} data-id="element-96">
                    <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${percentage}%`
                }} transition={{
                  duration: 1,
                  ease: 'easeOut'
                }} className={`absolute top-0 left-0 h-full opacity-10 ${barColor}`} data-id="element-97" />
                    <div className={`relative z-10 px-2 ${compact ? 'py-1' : 'py-1.5'}`} data-id="element-98">
                      <div className="flex items-center justify-between gap-1.5" data-id="element-99">
                        <div className="flex items-center gap-1.5" data-id="element-100">
                          <div className="shrink-0" data-id="element-101">{icon}</div>
                          <span className={`font-medium ${compact ? 'text-[10px]' : 'text-[11px]'} leading-tight ${textColor}`} data-id="element-102">
                            {option.text}
                          </span>
                        </div>
                        <span className={`font-bold ${compact ? 'text-[10px]' : 'text-[11px]'} text-brand-primary shrink-0`} data-id="element-103">
                          {percentage}%
                        </span>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {/* Right: Feedback */}
          <div className={`rounded-lg border-l-4 ${compact ? 'p-2.5' : 'p-3'} ${isCorrect ? 'bg-[#00857C]/10 border-[#00857C]' : 'bg-[#D64045]/10 border-[#D64045]'}`} data-id="element-104">
            <div className={`flex items-center ${compact ? 'gap-1.5 mb-1' : 'gap-2 mb-1.5'}`} data-id="element-105">
              <div className={`${compact ? 'p-0.5' : 'p-1'} rounded-full shrink-0 ${isCorrect ? 'bg-[#00857C]/20 text-[#00857C]' : 'bg-[#D64045]/20 text-[#D64045]'}`} data-id="element-106">
                {isCorrect ? <CheckCircle className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} data-id="element-107" /> : <XCircle className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} data-id="element-108" />}
              </div>
              <h3 className={`${compact ? 'text-[11px]' : 'text-xs'} font-bold ${isCorrect ? 'text-[#00857C]' : 'text-[#D64045]'}`} data-id="element-109">
                {isCorrect ? 'Correct!' : 'Incorrect'}
              </h3>
            </div>

            <div className={`text-gray-600 ${compact ? 'text-[10px]' : 'text-[11px]'} leading-relaxed whitespace-pre-line`} data-id="element-110">
              {feedbackBody}
            </div>
          </div>
        </div>

        <div className={`flex justify-end ${compact ? 'mt-2' : 'mt-3'}`} data-id="element-111">
          <Button variant="primary" onClick={onNext} data-id="element-112">
            {isLastQuestion ? 'Learn More' : 'Next Question'}{' '}
            <ArrowRight className="w-4 h-4 ml-2 inline" data-id="element-113" />
          </Button>
        </div>
      </motion.div>
    </div>;
}